/**
 * runidea.ai — Phase 13 — Multifamily Unit-Mix grid.
 *
 * Editable rows for 2–4 unit small-MF deals. Each row collects:
 *   label, count, bedrooms, bathrooms, rent (per unit / mo), vacancy_pct, notes
 *
 * Live aggregate footer mirrors what the backend's aggregate_unit_mix()
 * helper computes — so the user sees their math BEFORE submit.
 *
 * Parent owns the canonical array. We emit a fully replaced array via
 * onChange whenever a row is added / removed / mutated.
 */
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFmt } from "@/lib/useFmt";
import { formatPsfPsm } from "@/lib/priceNormalisation";

const DEFAULT_ROW = () => ({
  // Phase 46 Batch D — stable React key set at row creation so
  // reordering / removing rows doesn't cause controlled-input state
  // to leak into a neighbouring row.
  _key:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `um-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  label: "Unit",
  count: 1,
  bedrooms: 2,
  bathrooms: 1,
  rent: 0,
  vacancy_pct: 5,
  notes: "",
});

function rowAggregate(rows) {
  let totalUnits = 0;
  let potential = 0;
  let effective = 0;
  let vacWeighted = 0;
  for (const r of rows || []) {
    const c = Math.max(0, Number(r.count) || 0);
    const rent = Math.max(0, Number(r.rent) || 0);
    const v = Math.max(0, Number(r.vacancy_pct) || 0);
    totalUnits += c;
    const gross = c * rent;
    potential += gross;
    effective += gross * (1 - v / 100);
    vacWeighted += v * c;
  }
  return {
    totalUnits,
    potential,
    effective,
    avgVacancy: totalUnits > 0 ? vacWeighted / totalUnits : 0,
  };
}

export default function MultifamilyUnitMixGrid({ value, onChange, onAggregateChange, buildingPrice = 0, buildingSqft = 0 }) {
  const fmt = useFmt();
  const rows = Array.isArray(value) && value.length > 0 ? value : [DEFAULT_ROW()];
  const agg = rowAggregate(rows);
  // Phase 47.3 — building-level $/sqft + $/sqm subtitle. Computed off the
  // property's asking price and the building's gross square footage
  // (per-unit sqft isn't captured in the unit-mix schema yet — that's a
  // future enhancement). Falls back to null when either input is unset.
  const buildingPsfPsm =
    buildingPrice > 0 && buildingSqft >= 50
      ? formatPsfPsm(buildingPrice, buildingSqft)
      : null;

  // Surface the live totals up so the parent can write them into
  // analysis.monthly_rent (= effective_rent) without re-deriving.
  if (onAggregateChange) {
    // setState-in-render guard — only call when totals actually change.
    // Parent should memoise their callback. Safe to call every render.
    onAggregateChange(agg);
  }

  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, DEFAULT_ROW()]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3" data-testid="unit-mix-grid">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Unit mix</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Add one row per unit-type. Rent is <strong>per-unit / month</strong>.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
          data-testid="unit-mix-add-row"
          className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add row
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Label</th>
                <th className="px-3 py-2 text-right font-medium">Units</th>
                <th className="px-3 py-2 text-right font-medium">Beds</th>
                <th className="px-3 py-2 text-right font-medium">Baths</th>
                <th className="px-3 py-2 text-right font-medium">Rent / mo</th>
                <th className="px-3 py-2 text-right font-medium">Vacancy %</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {rows.map((r, i) => (
                <tr
                  // Phase 46 Batch D — stable `_key` from DEFAULT_ROW().
                  // Legacy rows (no `_key`) fall back on a composite of
                  // index + label so already-persisted drafts don't
                  // break.
                  key={r._key || `legacy-${i}-${r.label || ""}`}
                  data-testid={`unit-mix-row-${i}`}
                >
                  <td className="px-2 py-1.5">
                    <Input
                      data-testid={`unit-mix-row-${i}-label`}
                      value={r.label || ""}
                      onChange={(e) => update(i, { label: e.target.value })}
                      className="h-8 border-slate-200 text-sm"
                      placeholder="e.g. 2BR/1BA"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      data-testid={`unit-mix-row-${i}-count`}
                      type="number" min="0"
                      value={r.count ?? 0}
                      onChange={(e) => update(i, { count: Number(e.target.value) || 0 })}
                      className="h-8 w-20 border-slate-200 text-right text-sm tabular-nums"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      data-testid={`unit-mix-row-${i}-beds`}
                      type="number" min="0" step="0.5"
                      value={r.bedrooms ?? 0}
                      onChange={(e) => update(i, { bedrooms: Number(e.target.value) || 0 })}
                      className="h-8 w-20 border-slate-200 text-right text-sm tabular-nums"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      data-testid={`unit-mix-row-${i}-baths`}
                      type="number" min="0" step="0.5"
                      value={r.bathrooms ?? 0}
                      onChange={(e) => update(i, { bathrooms: Number(e.target.value) || 0 })}
                      className="h-8 w-20 border-slate-200 text-right text-sm tabular-nums"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      data-testid={`unit-mix-row-${i}-rent`}
                      type="number" min="0"
                      value={r.rent ?? 0}
                      onChange={(e) => update(i, { rent: Number(e.target.value) || 0 })}
                      className="h-8 w-28 border-slate-200 text-right text-sm tabular-nums"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      data-testid={`unit-mix-row-${i}-vacancy`}
                      type="number" min="0" max="100"
                      value={r.vacancy_pct ?? 0}
                      onChange={(e) => update(i, { vacancy_pct: Number(e.target.value) || 0 })}
                      className="h-8 w-20 border-slate-200 text-right text-sm tabular-nums"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(i)}
                      data-testid={`unit-mix-row-${i}-remove`}
                      disabled={rows.length <= 1}
                      className="h-7 w-7 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 text-xs">
              <tr>
                <td className="px-3 py-2 font-semibold text-slate-700">Totals</td>
                <td className="px-3 py-2 text-right font-bold tabular-nums text-slate-900" data-testid="unit-mix-total-units">
                  {agg.totalUnits}
                </td>
                <td colSpan="2" className="px-3 py-2 text-right text-slate-500">
                  Avg vacancy {agg.avgVacancy.toFixed(1)}%
                </td>
                <td className="px-3 py-2 text-right" data-testid="unit-mix-potential-rent">
                  <span className="block font-bold tabular-nums text-slate-900">
                    {fmt.money(agg.potential, { cents: false })}
                  </span>
                  <span className="block text-[10px] uppercase tracking-wider text-slate-400">potential</span>
                </td>
                <td className="px-3 py-2 text-right" data-testid="unit-mix-effective-rent">
                  <span className="block font-bold tabular-nums text-emerald-700">
                    {fmt.money(agg.effective, { cents: false })}
                  </span>
                  <span className="block text-[10px] uppercase tracking-wider text-slate-400">effective</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        The wizard will pass <strong>effective rent</strong> as the property&apos;s blended
        monthly rent into the rental engine.
      </p>
      {buildingPsfPsm ? (
        <p
          data-testid="unit-mix-building-psf"
          className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600"
        >
          Building basis: {buildingPsfPsm} (asking ÷ total sqft)
        </p>
      ) : null}
    </div>
  );
}
